// src/pages/GoldSchemeEnrollmentList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
} from "react-icons/fa";
import { TbArrowsSort } from "react-icons/tb";
import { FiRotateCcw } from "react-icons/fi";
import Select from "react-select";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import ConfirmDialog from "../components/ConfirmDialog";
import { handleDropDowns } from "../config/dropDownHelpers";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import {
  useGetAllGoldSchemesQuery,
  useGetAllGoldSchemeEnrollmentsQuery,
  useToggleGoldSchemeEnrollmentActiveMutation,
  useDeleteGoldSchemeEnrollmentMutation,
} from "../toolkitServices/goldSchemeApi";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

/* ------------ react-select styles (compact) ------------ */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #7c3aed33" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
  }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
    fontSize: "0.8rem",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#111827",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 34,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
        ? "#ddd6fe"
        : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 6,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused
      ? "0 0 0 1px rgba(148,129,206,.25)"
      : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const headerCell =
  "px-3 sm:px-4 py-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide";
const dataCell = "px-3 sm:px-4 py-1.5 text-xs sm:text-sm";

const toTitle = (s = "") =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

/* ------------ skeleton rows ------------ */
const SkeletonRows = ({ cols = 13, rows = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${r}-${c}`} className={dataCell}>
            <div className="h-3 w-20 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/* =======================================================================
   MAIN COMPONENT
   ======================================================================= */
const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};

const GoldSchemeEnrollmentList = () => {
  const navigate = useNavigate();
  const authUser = useSelector((s) => s.auth?.user);
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  /* ------------ table state ------------ */
  const [search, setSearch] = useState("");
  const [schemeFilter, setSchemeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("true"); // "true" | "false" | ""
  const [branchFilter, setBranchFilter] = useState(authUser?.branchId || "");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [sortBy, setSortBy] = useState("startDate");
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [confirm, setConfirm] = useState({
    open: false,
    mode: null, // 'delete'
    payload: null, // { id, name }
  });

  /* ------------ branches for filter ------------ */
  const { data: branchesRes } = useGetAllBranchesQuery();
  const branchOptions = useMemo(
    () =>
      handleDropDowns({
        key: "branch",
        branches: branchesRes?.data || branchesRes?.items || [],
      }),
    [branchesRes],
  );

  const branchValue = useMemo(
    () =>
      branchOptions.find(
        (o) => String(o.value) === String(branchFilter || authUser?.branchId),
      ) || null,
    [branchOptions, branchFilter, authUser?.branchId],
  );

  /* ------------ schemes for filter ------------ */
  const { data: schemesRes } = useGetAllGoldSchemesQuery({
    page: 1,
    limit: 1000,
    isActive: true,
    branchId: branchFilter || authUser?.branchId,
  });

  const schemes = useMemo(
    () => (Array.isArray(schemesRes?.data) ? schemesRes.data : []),
    [schemesRes],
  );

  const schemeOptions = useMemo(
    () =>
      schemes.map((s) => ({
        value: s._id || s.id,
        label: `${s.schemeName} ${s.schemeType ? `(${s.schemeType})` : ""}${
          s.schemeCode ? ` • ${s.schemeCode}` : ""
        }`,
        raw: s,
      })),
    [schemes],
  );

  const schemeValue = useMemo(
    () => schemeOptions.find((o) => String(o.value) === String(schemeFilter)),
    [schemeOptions, schemeFilter],
  );

  /* ------------ query: enrollment list ------------ */
  const { data, isFetching, isSuccess } = useGetAllGoldSchemeEnrollmentsQuery({
    page: 1,
    limit: 1000,
    search,
    branchId: branchFilter || authUser?.branchId,
    schemeId: schemeFilter || undefined,
    isActive:
      statusFilter === "" ? undefined : statusFilter === "true" ? true : false,
    tenantId: authUser?.tenantId,
  });

  const enrollments = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data],
  );

  /* ------------ local sort ------------ */
  const sortedEnrollments = useMemo(() => {
    const copy = [...enrollments];

    const getVal = (e) => {
      switch (sortBy) {
        case "customer":
          return (
            (e.customerName ||
              e.customer?.name ||
              e.customer?.customerName ||
              "") + ""
          ).toLowerCase();
        case "scheme":
          return (
            (e.schemeName || e.scheme?.schemeName || "") + ""
          ).toLowerCase();
        case "startDate":
          return e.startDate ? new Date(e.startDate).getTime() : 0;
        case "maturityDate":
          return e.maturityDate ? new Date(e.maturityDate).getTime() : 0;
        case "monthlyAmount":
          return Number(e.monthlyAmount) || 0;
        case "paidInstallments":
          return Number(e.totalPaidInstallments) || 0;
        case "paidAmount":
          return Number(e.totalPaidAmount) || 0;
        case "status":
          return e.isActive ? 1 : 0;
        default:
          return e.createdAt ? new Date(e.createdAt).getTime() : 0;
      }
    };

    copy.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return copy;
  }, [enrollments, sortBy, sortOrder]);

  /* ------------ pagination on FE ------------ */
  const total = sortedEnrollments.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const rows = sortedEnrollments.slice(startIndex, endIndex);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/scheme/gold-scheme/enroll/new");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  useEffect(() => {
    setPage(1);
  }, [search, schemeFilter, statusFilter, branchFilter, limit]);

  /* ------------ RTK mutations ------------ */
  const [toggleActive, { isLoading: toggling }] =
    useToggleGoldSchemeEnrollmentActiveMutation();
  const [deleteEnrollment, { isLoading: deleting }] =
    useDeleteGoldSchemeEnrollmentMutation();

  /* ------------ helpers ------------ */
  const onSort = (field) => {
    if (sortBy === field) {
      setSortOrder((s) => (s === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortIcon = (field) => (
    <span
      className="inline-flex items-center gap-1 cursor-pointer select-none"
      onClick={() => onSort(field)}
      title="Sort"
    >
      <TbArrowsSort className="text-[16px]" />
      {sortBy === field ? (
        <span className="text-[10px]">{sortOrder.toUpperCase()}</span>
      ) : null}
    </span>
  );

  const openDeleteConfirm = (enrollment) => {
    const name =
      enrollment.customerName ||
      enrollment.customer?.name ||
      enrollment.customer?.customerName ||
      "";
    setConfirm({
      open: true,
      mode: "delete",
      payload: {
        id: enrollment._id,
        name: name || enrollment.enrollmentCode || "this enrollment",
      },
    });
  };

  const closeConfirm = () =>
    setConfirm({ open: false, mode: null, payload: null });

  const handleConfirm = async () => {
    if (confirm.mode !== "delete" || !confirm.payload?.id) return;
    try {
      await deleteEnrollment({
        id: confirm.payload.id,
        branchId: branchFilter || authUser?.branchId,
        tenantId: authUser?.tenantId,
      }).unwrap();
      toast.success("Enrollment deleted successfully");
    } catch (err) {
      const msg =
        err?.data?.message || err?.data?.error || "Failed to delete enrollment";
      toast.error(msg);
    } finally {
      closeConfirm();
    }
  };

  const handleToggleActive = async (enrollment) => {
    try {
      await toggleActive({
        id: enrollment._id,
        branchId: branchFilter || authUser?.branchId,
        tenantId: authUser?.tenantId,
      }).unwrap();
      toast.success(
        `Enrollment ${
          enrollment.isActive ? "deactivated" : "activated"
        } successfully`,
      );
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.error ||
        "Failed to update enrollment status";
      toast.error(msg);
    }
  };

  /* ------------ applied filter chips ------------ */
  const chips = useMemo(() => {
    const arr = [];
    if (search) arr.push({ key: "search", label: `Search: ${search}` });
    if (schemeValue?.label)
      arr.push({ key: "scheme", label: `Scheme: ${schemeValue.label}` });
    if (statusFilter !== "")
      arr.push({
        key: "status",
        label: `Status: ${statusFilter === "true" ? "Active" : "Inactive"}`,
      });
    if (branchValue?.label)
      arr.push({ key: "branch", label: `Branch: ${branchValue.label}` });
    return arr;
  }, [search, schemeValue, statusFilter, branchValue]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
    else if (key === "scheme") setSchemeFilter("");
    else if (key === "status") setStatusFilter("");
    else if (key === "branch") setBranchFilter("");
  };

  const clearAllChips = () => {
    setSearch("");
    setSchemeFilter("");
    setStatusFilter("true");
    setBranchFilter(authUser?.branchId || "");
  };

  const resetFilters = () => {
    setSchemeFilter("");
    setStatusFilter("true");
    setBranchFilter(authUser?.branchId || "");
    setSearch("");
  };

  const formatDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (val) =>
    (Number(val) || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });

  return (
    <div className="relative bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {/* global loader */}
      {isFetching && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/50 pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading enrollments…</span>
          </div>
        </div>
      )}

      {/* header bar (slightly tighter on mobile) */}
      <div className="flex items-center justify-between px-4 py-2 rounded-t-xl bg-[#9481ce] text-white">
        <div className="flex items-center gap-2">
          <h2 className="text-base sm:text-lg font-semibold tracking-tight">
            Scheme Enrollments
          </h2>
          {isFetching && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>
          <Link
            to="/scheme/gold-scheme/enroll/new"
            className="inline-flex items-center gap-1 rounded-full bg-white text-[#6b4fd8] px-3 sm:px-4 py-1.5 text-[11px] sm:text-xs font-semibold shadow-sm hover:bg-violet-50 transition"
          >
            + Add New
          </Link>
        </div>
      </div>

      {/* top filters bar
          ✅ Mobile: only Search + Filters
          ✅ Desktop: full filters
      */}
      <div className="px-3 sm:px-4 py-1.5 sm:py-2 border-b bg-slate-50/80">
        {/* MOBILE quick row */}
        <div className="flex items-center gap-2 sm:hidden">
          <input
            type="text"
            placeholder="Search customer / mobile / code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
          <button
            onClick={() => setIsFilterOpen(true)}
            className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-violet-200 bg-white text-[#6b59c6] outline-[#6b59c6] hover:bg-violet-50"
            title="Advanced filters"
            aria-label="Advanced filters"
          >
            <FaFilter />
          </button>
        </div>

        {/* DESKTOP full row */}
        <div className="hidden sm:flex flex-wrap items-end gap-3">
          {/* Scheme filter */}
          <div className="flex flex-col gap-[1px] w-[240px]">
            <label className="text-[11px] font-medium text-slate-600">
              Scheme
            </label>
            <Select
              styles={selectStyles}
              value={schemeValue || null}
              options={schemeOptions}
              isClearable
              onChange={(opt) => setSchemeFilter(opt?.value || "")}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>

          {/* Status */}
          <div className="flex flex-col gap-[1px] w-[140px]">
            <label className="text-[11px] font-medium text-slate-600">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
              <option value="">All</option>
            </select>
          </div>

          {/* Branch */}
          <div className="flex flex-col gap-[1px] w-[190px]">
            <label className="text-[11px] font-medium text-slate-600">
              Branch
            </label>
            <Select
              styles={selectStyles}
              value={branchValue}
              options={branchOptions}
              isClearable
              onChange={(opt) => setBranchFilter(opt?.value || "")}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>

          {/* Search */}
          <div className="flex flex-col gap-[1px] flex-1 min-w-[200px]">
            <label className="text-[11px] font-medium text-slate-600">
              Search
            </label>
            <input
              type="text"
              placeholder="Customer name / mobile / enrollment code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Filter button */}
          <div className="flex flex-col gap-[1px] w-auto">
            <label className="text-[11px] font-medium text-slate-600 invisible">
              Filters
            </label>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex items-center gap-1 bg-white text-[#6b59c6] px-3 py-1.5 rounded-full text-[11px] border border-violet-200 hover:bg-violet-50 outline-[#6b59c6]"
              title="Advanced filters"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* active chips (mobile: horizontal scroll to save height) */}
      {chips.length > 0 && (
        <div className="px-3 sm:px-4 py-1.5 border-b bg-slate-50 flex items-center gap-2">
          <div className="flex items-center gap-2 flex-nowrap overflow-x-auto min-w-0 pr-2">
            {chips.map((c) => (
              <span
                key={c.key}
                className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm whitespace-nowrap flex-shrink-0"
              >
                {c.label}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => clearChip(c.key)}
                  title="Clear"
                >
                  <FaTimesCircle />
                </button>
              </span>
            ))}
          </div>

          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline flex-shrink-0"
            onClick={clearAllChips}
            title="Clear all"
          >
            Clear all
          </button>
        </div>
      )}

      {/* table (✅ flex-1 for max height) */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto">
        <table className="min-w-full text-xs sm:text-sm text-left">
          <thead className="sticky top-0 z-40 bg-[#e6e1f5] text-[10px] uppercase text-[#4b3d91] sm:text-[11px]">
            <tr>
              <th className={headerCell}>#</th>
              <th className={headerCell}>Enroll Code</th>
              <th className={headerCell}>Customer</th>
              <th className={headerCell}>Scheme</th>
              <th className={headerCell}>Type</th>
              <th className={headerCell}>Monthly Amt</th>
              <th className={headerCell}>Start Date {sortIcon("startDate")}</th>
              <th className={headerCell}>
                Maturity Date {sortIcon("maturityDate")}
              </th>
              <th className={headerCell}>Installments</th>
              <th className={headerCell}>Paid Amt</th>
              <th className={headerCell}>Metal Credited</th>
              <th className={headerCell}>Remaining Amt</th>
              <th className={headerCell}>Branch</th>
              <th className={`${headerCell} sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px]`}><span className="sm:hidden">Act</span><span className="hidden sm:inline">Action</span></th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence initial={false}>
              {isFetching && !isSuccess ? (
                <SkeletonRows cols={13} rows={6} />
              ) : rows.length ? (
                rows.map((e, idx) => {
                  const rowIndex = startIndex + idx + 1;
                  const scheme = e.scheme || {};
                  const customer = e.customer || {};

                  const schemeType = scheme.schemeType || e.schemeType;

                  const branchName =
                    e.branchName ||
                    e.branch?.branchName ||
                    e.branch?.name ||
                    branchOptions.find(
                      (b) => String(b.value) === String(e.branchId),
                    )?.label ||
                    "-";

                  const custName =
                    e.customerName ||
                    customer.name ||
                    customer.customerName ||
                    "-";

                  const mobile =
                    e.customerMobile ||
                    customer.mobileNumber ||
                    customer.phone ||
                    "";

                  const totalInstallments = Number(e.installmentMonths) || 0;
                  const paidInstallments = Number(e.totalPaidInstallments) || 0;

                  const totalPaidAmount = Number(e.totalPaidAmount) || 0;
                  const canEditOrDelete = totalPaidAmount === 0;

                  return (
                    <motion.tr
                      key={e._id || idx}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, delay: idx * 0.02 }}
                      className="group border-t transition hover:bg-[#f4f0ff]"
                    >
                      <td className={dataCell}>{rowIndex}</td>

                      <td className={dataCell}>{e.enrollmentCode || "-"}</td>

                      <td className={`${dataCell} font-medium`}>
                        {custName}
                        {mobile ? (
                          <span className="block text-[10px] text-slate-500">
                            {mobile}
                          </span>
                        ) : null}
                      </td>

                      <td className={dataCell}>
                        {scheme.schemeName || e.schemeName || "-"}
                        {scheme.schemeCode && (
                          <span className="block text-[10px] text-slate-500">
                            {scheme.schemeCode}
                          </span>
                        )}
                      </td>

                      <td className={dataCell}>
                        {schemeType ? (
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] ${
                              schemeType === "AMOUNT"
                                ? "bg-amber-50 text-amber-700"
                                : "bg-sky-50 text-sky-700"
                            }`}
                          >
                            {toTitle(schemeType)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className={dataCell}>
                        ₹ {formatCurrency(e.schemeAmountPerMonth)}
                      </td>

                      <td className={dataCell}>{formatDate(e.startDate)}</td>

                      <td className={dataCell}>{formatDate(e.maturityDate)}</td>

                      <td className={dataCell}>
                        {paidInstallments} / {totalInstallments}
                      </td>

                      <td className={dataCell}>
                        ₹ {formatCurrency(e.totalPaidAmount)}
                      </td>

                      <td className={dataCell}>
                        {formatCurrency(e.totalMetalCredited)} gr
                      </td>

                      <td className={dataCell}>
                        ₹ {formatCurrency(e.remainingAmount)}
                      </td>

                      <td className={dataCell}>{branchName}</td>

                      <td
                        className={`${dataCell} sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px]`}
                      >
                        

                        <motion.button
                          whileHover={canEditOrDelete ? { scale: 1.05 } : {}}
                          whileTap={canEditOrDelete ? { scale: 0.95 } : {}}
                          disabled={!canEditOrDelete || deleting}
                          className={
                            "inline-flex items-center justify-center text-rose-500 text-[11px] p-1.5 rounded-full hover:bg-rose-50 disabled:opacity-40 " +
                            (!canEditOrDelete
                              ? "opacity-40 cursor-not-allowed hover:bg-transparent"
                              : "")
                          }
                          title={
                            canEditOrDelete
                              ? "Delete"
                              : "Cannot delete: payments already received"
                          }
                          onClick={() => {
                            if (canDelete(user)) {
                              if (canEditOrDelete) {
                                openDeleteConfirm(e);
                              }
                            } else {
                              toast.error(
                                "You don't have permission to delete",
                              );
                            }
                          }}
                        >
                          <FaTrashAlt />
                        </motion.button>
                      </td>
                    </motion.tr>
                  );
                })
              ) : (
                <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <td
                    colSpan={13}
                    className="px-4 py-8 text-center text-xs text-slate-500"
                  >
                    No enrollments found. Try changing filters or add a new
                    enrollment.
                  </td>
                </motion.tr>
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* footer pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-slate-50 px-2 py-1.5 text-[10px] sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px]">
        <div className="hidden items-center gap-2 sm:flex">
          <span className="text-slate-600">
            Showing{" "}
            <span className="font-semibold">
              {rows.length ? startIndex + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {rows.length ? startIndex + rows.length : 0}
            </span>{" "}
            of <span className="font-semibold">{total}</span> enrollments
          </span>
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <span className="hidden text-slate-500 sm:inline">Rows:</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[5, 10, 20, 50].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:w-auto sm:flex-none sm:justify-start sm:gap-1">
            <button
              type="button"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &lsaquo;
              </span>
              <span className="hidden sm:inline">Prev</span>
            </button>
            <span className="text-slate-600">
              <span className="flex items-center gap-1 sm:hidden">
                <Select
                  value={{ value: page, label: String(page) }}
                  options={Array.from({ length: totalPages }, (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }))}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[52px]"
                  aria-label="Select page number"
                />
                <span>/ {totalPages}</span>
              </span>
              <span className="hidden items-center gap-1 sm:flex">
                <span>Page</span>
                <Select
                  value={{ value: page, label: String(page) }}
                  options={Array.from({ length: Math.max(1, totalPages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[58px]"
                  aria-label="Select page number"
                />
                <span>of {Math.max(1, totalPages)}</span>
              </span>
            </span>
            <button
              type="button"
              disabled={page >= totalPages || isFetching}
              onClick={() =>
                setPage((p) => (totalPages ? Math.min(totalPages, p + 1) : p))
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &rsaquo;
              </span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filter modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Advanced filters
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Filter enrollments by scheme, status and branch.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 space-y-4 text-[11px] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">Scheme</label>
                    <Select
                      styles={selectStyles}
                      value={schemeValue}
                      options={schemeOptions}
                      isClearable
                      onChange={(opt) => setSchemeFilter(opt?.value || "")}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 mb-1">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                      <option value="">All</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-slate-600 mb-1">Branch</label>
                    <Select
                      styles={selectStyles}
                      value={branchValue}
                      options={branchOptions}
                      isClearable
                      onChange={(opt) => setBranchFilter(opt?.value || "")}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={confirm.open}
        title="Delete this enrollment?"
        description={`Are you sure you want to delete enrollment "${
          confirm.payload?.name || ""
        }"?`}
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        loading={deleting}
      />
    </div>
  );
};

export default GoldSchemeEnrollmentList;
